<template>
  <div class="md-layout">
    <div v-if="$route.params.id" id="objectHeaderContentHidden" style="display: none;">
      <div><md-icon class="icon-color">{{formConfig.headerIcon}}</md-icon></div>
      <div>
        <h1 class="md-title icon-color content-wrap">{{recordData? recordData.given_name + ' ' + recordData.family_name : ''}}</h1>
      </div>
    </div>

    <!-- submit button -->
    <md-button
      type="submit"
      :disabled="$v.form.$invalid || isPromisePending"
      v-on:click="resendSignupCode"
      v-show="canResendSignup"
      class="atlas-button-blue"
    >
      Resend Temporary Password
    </md-button>
    <!-- end submit button -->

    <template
      v-if="$route.params.id">
      <md-tabs md-sync-route class="topNavTabs" :class="[
        { 'edit-mode': formConfig.pageMode == 'new' || formConfig.pageMode == 'edit' },
      ]">

        <!-- User View/Edit form -->
        <md-tab id="tab-user" md-label="User" :to="recordPath" exact>
          <div class="md-card md-layout-item md-small-size-100">
            <CustomUserForm :atShowAuditLog="onShowAuditLog" :update="updateForm" :form="form" :$v="$v" :formConfig="formConfig" :headerColor="headerColor"></CustomUserForm>
          </div>
        </md-tab>

        <!-- Team tab -->
        <md-tab id="tab-team" md-label="Team" :to="recordPath + '/teams'">
          <ElasticList 
            ref="teamsListRef"
            v-if="$route && $route.meta.tab === 'teams'" 
            :list.sync="teamsList" 
            :filter="teamUserUserId">
          </ElasticList>

          <md-dialog :md-active.sync="teamsList.showRelatedDialog">
            <md-dialog-title>{{relateTeamList.pageTitle}}</md-dialog-title>
            <md-dialog-content>

              <ElasticList
                v-if="recordData"
                :list.sync="relateTeamList"
                :exclude="relateUserTeamFilter"
                :relateRecordInput="{teamUserTeamId: null,  teamUserUserId: $route.params.id}"
                relateRecordInputField="teamUserTeamId"
              ></ElasticList>

            </md-dialog-content>
            <md-dialog-actions>
              <md-button class="md-theme-default" @click="teamsList.showRelatedDialog = false">Close</md-button>
            </md-dialog-actions>
          </md-dialog>
        </md-tab>

      </md-tabs>
    </template>

    <div v-else class="md-tabs topNavTabs md-alignment-left md-theme-default" :class="[
        { 'edit-mode': formConfig.pageMode == 'new' || formConfig.pageMode == 'edit' },
      ]"> 
      <div class="md-content md-tabs-content md-theme-default">
        <div class="md-tabs-container">
          <div id="tab-active" class="md-tab">
            <div class="md-card md-layout-item md-small-size-100">
              <CustomUserForm :update="updateForm" :form="form" :$v="$v" :formConfig="formConfig" :headerColor="headerColor"></CustomUserForm>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Audit Log dialog -->
    <template v-if="formConfig && 'view' == formConfig.pageMode">
      <md-dialog :md-active.sync="auditLogDialog" class="auditLogModal">
        <md-dialog-title>Audit Log</md-dialog-title>
        <md-dialog-content>
          <AuditLogDialog :list.sync="auditLogList" :filter.sync="auditLogFilter" ref="auditLogListRef"
          ></AuditLogDialog>
        </md-dialog-content>
        <md-dialog-actions>
          <md-button class="md-theme-default" @click="auditLogDialog = false">Close</md-button>
        </md-dialog-actions>
      </md-dialog>
    </template>

    <!-- alert dialog -->
    <md-dialog-alert
      class="_resendSignupCode"
      :md-active.sync="isAlertModalActive"
      :md-content="alertModalMessage"
      md-confirm-text="Ok!"
      @md-closed="onAlertModalClose"
    />
    <!-- end alert dialog -->

    <!-- loading spinner -->
    <div class="dynamicList-loading" v-show="isPromisePending">
      <md-progress-spinner
        :md-diameter="30"
        :md-stroke="3"
        md-mode="indeterminate"
      ></md-progress-spinner>
    </div>
    <!-- end loading spinner -->

  </div>
</template>

<script>
  import _ from "lodash";
  import { Auth } from "aws-amplify";
  import AWS from 'aws-sdk';
  import aws_exports from "../../aws-exports";
  import Record from '@/mixins/Record';
  import AuditLogDialog from "@/components/DynamicForm/AuditLogDialog";
  import moment from 'moment';

  // Form validations
  import { required, numeric, minLength } from "vuelidate/lib/validators";

  export default {
    name: "product",
    mixins: [Record],
    components: {
      AuditLogDialog
    },
    computed: {
      canResendSignup() {
        return (
          this.form
          && this.form.cogUserStatus === 'FORCE_CHANGE_PASSWORD'
          && this.formConfig.pageMode === 'view'
        );
      },
      auditLogFilter() {
        return [
          { match_phrase: { modelName: 'User' } },
          { match_phrase: { modelId: this.$route.params.id } },
        ];
      },
      teamUserUserId: function() { return { match_phrase: { 'teamUserUserId': this.$route.params.id } }; },
      personCompanyPersonId: function () { return {"personCompanyPersonId": {"eq": this.$route.params.id }}; },
      relateCompanyPersonFilter: function () {
        let result = null;
        if (this.recordData && this.recordData.companys && this.recordData.companys.items) {
          let companys = this.recordData.companys.items;
          let filters = [];

          companys.forEach(item => {
            filters.push({"id": {"ne": item.id}});
          });
          if (filters.length) {
            result = {"and": filters};
          }
        }

        return result;
      },
      relateUserTeamFilter: function() {
        let result = null;
        if (this.recordData && this.recordData.teams && this.recordData.teams.items) {
          let items = this.recordData.teams.items;
          let filters = [];
          items.forEach(item => {
            filters.push({"match": {"cognito_groupid": item.cognito_groupid}});
          });
          if (filters.length) {
            result = {"must_not": filters};
          }
        } 
        return result;
      },
    },
    data() {
      return {
        isPromisePending: false,
        isAlertModalActive: false,
        alertModalMessage: '',
        auditLogDialog: false,
        auditLogList: {
          class: 'auditLogList',
          hideItemActions: true,
          hideListHeader: true,
          skipCardLayout: true,
          hideNewRecordButton: true,
          searchRecordsInput: false,
          hideActionColumn: true,
          pageTitle: "Change History",
          listQueryId: "searchChangesCustom",
          responseFieldName: "searchChanges",
          columns: {
            user: { label: "Changed By", sortable: false, size: 10 },
            fieldName: { label: "Field", sortable: false, size: 10 },
            oldValue: { label: "Old Value", sortable: false, size: 10 },
            newValue: { label: "New Value", sortable: false, size: 10 },
            createdAt: { label: "Change Date", sortable: true, dateFilter: true, size: 15 },
            //action: { label: "Activity", sortable: false, size: 10 },
            //change: { label: "Change", sortable: false, format: "json", size: 55 },
          },
          //hyperlink config based on admin role
          linkConfigs: this.$Session && this.$Session.isAdminGroupMember ? [
            {
              name: 'user',
              hrefCallback: (column, name, item) => {               
                return '#/users/' + (item.user ? item.userId : "" );
              },
              titleCallback: (column, name, item) => {                           
                  return item.user                
              },
            }
          ] : [],            
          currentSort: "createdAt",
          currentSortOrder: "asc",
          pagination: {
            perPage: 10,
            currentPage: 1,
            total: 0,
            perPageOptions: [5, 10, 20, 25, 50],
            perPageMaxLimit: 999
          },
          perPageOptions: [5, 10, 20, 25, 50],
          defaults: {
            sort: 'createdAt',
            sortDir: 'desc',
            perPage: 10,
          },
          tableData: [],
          searchedData: [],
          fuseSearch: null,
          assigneeList: [],
          formatResponse: (data) => {
            let result = [];
            if (data && data.searchChanges && data.searchChanges.items.length) {
              let items = data.searchChanges.items;
              items.forEach((item) => {
                /** Format user and change for display */
                // item.userId = item.user ? item.user.id : '';
                // item.user = item.user ? item.user.given_name + ' ' + item.user.family_name : item.owner;
                // item.action = item.action ? item.action.charAt(0).toUpperCase() + item.action.slice(1) : '';
                // item.createdAt = moment(String(item.createdAt)).format('MMM DD, YYYY h:mm:ss a')                
                if (item.change)  {
                  
                  //let change = JSON.stringify(JSON.parse(item.change),null,2);
                  let parseItem = JSON.parse(item.change);
                  item.userId = item.user ? item.user.id : '';
                  item.user = item.user ? item.user.given_name + ' ' + item.user.family_name : item.owner;
                  
                  Object.keys(parseItem).forEach((k) => {
                    if (k !== "old") {
                      let newVal = parseItem[k];
                      let oldVal = "";
                      if (typeof parseItem[k] === 'object') {
                        newVal = JSON.stringify(parseItem[k]);
                      }

                      //if old field exist set oldValue
                      if (parseItem.old) {
                        oldVal = parseItem.old[k];
                        if (typeof parseItem.old[k] === 'object') {
                          oldVal = JSON.stringify(parseItem.old[k]);
                        }
                      }

                      const newItem = {
                        fieldName: k,
                        newValue: newVal,
                        oldValue: oldVal,
                        userId: item.userId,
                        user: item.user,
                        //action: item.action ? item.action.charAt(0).toUpperCase() + item.action.slice(1) : '',
                        createdAt: moment(String(item.createdAt)).format('MMM DD, YYYY h:mm:ss a'),
                        //change: change
                      };
                      result.push(newItem);
                    }
                  });
                } 
                //result.push(item);
              })
            }
            return this.orderedAuditLog(result)
          },
        },
        formConfig: {
          hasUpdatedBy: true,
          updatedByFieldName: 'userUpdatedById',
          responseFieldName: 'getUser',
          actions: {
            save: 'createUser',
            view: 'getUser',
            edit: 'getUser',
            new: 'getUser',
            update: 'updateUser',
            delete: 'deleteUser',
          },
          slots: {
            basic: {label: 'Basic Information', icon: 'work', layout: {"x":0,"y":0,"w":12,"h":12,"i":"0"}},
            role: {label: 'Role Information', icon: 'work', editModeOnly: true, layout: {"x":0,"y":12,"w":12,"h":12,"i":"1"}},
          },
          listPath: "/users",
          headerIcon: 'emoji_people',
          objectName: 'User',
          objectNamePlural: 'Users',
          editMode: false,
          pageMode: 'new', // can be one of ['new', 'view' 'edit']

          fields: {
            id: { label: "ID", type: "id", slot: 'basic', value: "" },
            cogEmailVerified: { label: "Email Verified", type: "checkbox", slot: 'basic', preventEdit: true },
            cogUserStatus: { label: "Cognito Status", type: "select", slot: 'basic', preventEdit: true,  options: [
              {name: 'UNCONFIRMED', id: 'UNCONFIRMED'},
              {name: 'CONFIRMED', id: 'CONFIRMED'},
              {name: 'ARCHIVED', id: 'ARCHIVED'},
              {name: 'COMPROMISED', id: 'COMPROMISED'},
              {name: 'UNKNOWN', id: 'UNKNOWN'},
              {name: 'RESET_REQUIRED', id: 'RESET_REQUIRED'},
              {name: 'FORCE_CHANGE_PASSWORD', id: 'FORCE_CHANGE_PASSWORD'},
            ] },
            cogUsername: { label: "Cognito ID", type: "text", slot: 'basic', preventEdit: true },
            email: { label: "Email", type: "text", slot: 'basic', preventEdit: false },
            given_name: { label: "First Name", type: "text", slot: 'basic' },
            family_name: { label: "Last Name", type: "text", slot: 'basic' },
            source: { label: "Source", type: "text", slot: 'basic', preventEdit: true },
            createdAt: { label: "Created", type: "text", slot: 'basic', preventEdit: true },
            updatedAt: { label: "Last Modified", type: "text", slot: 'basic', preventEdit: true },
            IPCorp: { label: "IPCorp", type: "checkbox", slot: 'role', preventEdit: false },
            internal: { label: "Internal", type: "checkbox", slot: 'role', preventEdit: false },
            TaskSaveAsTemplate: { label: "TaskSaveAsTemplate", type: "checkbox", slot: 'role', preventEdit: false },
            CaseSaveAsTemplate: { label: "CaseSaveAsTemplate", type: "checkbox", slot: 'role', preventEdit: false },
          },
          editTitleField: 'name'
        },
        form: {
          id: "",
          cogEmailVerified: "",
          cogUserStatus: "",
          cogUsername: "",
          email: "",
          given_name: "",
          family_name: "",
          source: "Admin",
          createdAt: "",
          updatedAt: "",
          IPCorp: "",
          internal: "",
          TaskSaveAsTemplate: "",
          CaseSaveAsTemplate: "",
        },
        teamsList: {
          refreshData: false,
          showRelatedDialog: false,
          itemActions: [
            {
              class: "md-just-icon md-warning md-simple",
              icon: "description",
              click: "handleEdit"
            },
            {
              class: "md-just-icon md-danger md-simple",
              icon: "close",
              click: "handleDelete"
            }
          ],
          editPath: "teams",
          pageTitle: "Teams",
          pageIcon: "style",
          listQueryId: "searchTeamUsers",
          deleteQueryId: "deleteTeamUser",
          deleteQueryIdField: "relateRecordId",
          deleteMessageTitle: "Are you sure you want to remove {objectName} from this Team?",
          deleteMessageText: "",
          deleteMessageNameFields: ['given_name'],
          userGroupId: "cognito_groupid",
          columns: {
            //given_name: { label: "First Name", sortable: false },
            //family_name: { label: "Last Name", sortable: false },
            role: { label: "Role", sortable: false },
            cognito_groupid: { label: "Name", sortable: false },
            createdAt: { label: "Created", sortable: false, dateFilter: true },
          },
          searchRecordsInput: false,
          currentSort: "createdAt",
          currentSortOrder: "asc",
          pagination: {
            perPage: 25,
            currentPage: 1,
            perPageOptions: [5, 10, 25, 50],
            total: 0,
            //perPageMaxLimit: 999
          },
          perPageOptions: [5, 10, 20, 25, 50],
          defaults: {
            sort: 'createdAt',
            sortDir: 'desc',
            perPage: 10,
          },
          tableData: [],
          searchedData: [],

          fuseSearch: null,
          formatResponse: (data) => {
            let result = [];
            if (data && data.searchTeamUsers && data.searchTeamUsers.items.length) {
              this.$c.log('formatResponse data', data.searchTeamUsers.items);
              data.searchTeamUsers.items.forEach((item) => { 
                item.team.relateRecordId = item.id;
                item.id = item.team.id; //for navigating to Team details page
                item.team.given_name = item.user.given_name;
                item.team.family_name = item.user.family_name;
                item.team.cognito_groupid = item.team.cognito_groupid.replace(/_/g, " ");
                item.team.role = 'Team ' + item.role;
                result.push(item.team);
              })
            }
            return result;
          },
        },
        relateTeamList: {
          hideListHeader: true,
          skipCardLayout: true,
          teamFroup:'',
          editPath: "team",
          pageTitle: "Search for Team",
          pageIcon: "style",
          listQueryId: "searchTeams",
          createMutationId: "createTeamUser",
          deleteQueryId: "deleteTeamUser",
          itemActions: [
            {
              class: "md-just-icon md-simple md-warning",
              classAdded: "item.added? 'check_circle' : 'md-warning'",
              icon: "post_add",
              iconAdded: "check_circle",
              click: "handleAddRole"
            }
          ],
          columns: {
            name: { label: "Name", sortable: true },
            createdAt: { label: "Created", sortable: true, dateFilter: true },
          },
          roleRecord: {
            Lead: "Team Lead", 
            Member: "Team Member",
            Watcher: "Team Watcher"
          },
          perPageOptions: [5, 10, 20, 25, 50],
          defaults: {
            sort: 'createdAt',
            sortDir: 'asc',
            perPage: 10,
          },
          fuseSearch: null,
          formatResponse: (data) => {
            this.$c.log(data);
            let result = [];
            let responseKey = 'searchTeams';
            if (data && data[responseKey] !== 'undefined') {
              let items = data[responseKey].items;
              items.forEach((item) => {
                result.push(item);
              })
            }
            return result;
          },
        },
        companysList: {
          showRelatedDialog: false,
          editPath: "companies",
          pageTitle: "Companies",
          pageIcon: "store",
          listQueryId: "listPersonCompanys",
          deleteQueryId: "deletePersonCompany",
          deleteQueryIdField: "relateRecordId",
          deleteMessageTitle: "Are you sure you want to remove {objectName} from this Person?",
          deleteMessageText: "",
          columns: {
            name: { label: "Name", sortable: true },
            phone: { label: "Phone", sortable: true },
            phoneExt: { label: "Phone Ext", sortable: true },
          },
          searchFields: ["name"],
          currentSort: "name",
          currentSortOrder: "asc",
          pagination: {
            perPage: 25,
            currentPage: 1,
            perPageOptions: [5, 10, 25, 50],
            total: 0
          },
          tableData: [],
          searchedData: [],
          fuseSearch: null,
          formatResponse: (data) => {

            let result = [];
            if (data && data.listPersonCompanys && data.listPersonCompanys.items.length) {
              this.$c.log('formatResponse data', data.items);
              data.listPersonCompanys.items.forEach((item) => {
                item.company.relateRecordId = item.id;
                result.push(item.company);
              })
            }
            return result;
          },
        },
        relateCompanysList: {
          hideListHeader: true,
          editPath: "companies",
          pageTitle: "Search for Company",
          pageIcon: "store",
          listQueryId: "listCompanys",
          createMutationId: "createPersonCompany",
          deleteQueryId: "deletePersonCompany",
          columns: {
            name: { label: "Name", sortable: true },
            phone: { label: "Phone", sortable: true },
          },
          searchFields: ["name", "phone", "phoneExt"],
          currentSort: "name",
          currentSortOrder: "asc",
          pagination: {
            perPage: 25,
            currentPage: 1,
            perPageOptions: [5, 10, 25, 50],
            total: 0
          },
          tableData: [],
          searchedData: [],
          fuseSearch: null,
          formatResponse: (data) => {
            let result = [];
            if (data && data.listCompanys && data.listCompanys.items.length) {
              result = data.listCompanys.items;
            }
            return result;
          },

        },
        subscriptions: {
          'onCreateTeamUser': {
            result: null,
            load: true,
            function: (eventData) => {
              this.$c.log('SUBSCRIPTION Trigger: onCreateTeamUser');
              let eventRecord = eventData.value.data.onCreateTeamUser;
              /** Filter out records that do not apply to the Portfolio we are looking at */
              if (this.recordData.id === eventRecord.user.id) {
                // refresh teams list after 1 sec
                setTimeout(() => {
                  this.$refs.teamsListRef.refresh();
                  this.formConfig.refreshRecordData = true;
                }, 1000);
              }
            }
          },
          'onDeleteTeamUser': { 
            result: null,
            load: true,
            function: (eventData) => { 
              this.$c.log('SUBSCRIPTION Trigger: onDeleteTeamUser');
              let eventRecord = eventData.value.data.onDeleteTeamUser;
              if (this.recordData.id === eventRecord.user.id) {
                // refresh teams list after 1 sec
                setTimeout(() => {
                  this.$refs.teamsListRef.refresh();
                  this.formConfig.refreshRecordData = true;
                }, 1000);
              }
            }
          },
          
        },
      }
    },
    validations() {
      return {
        form: {
          id: {value: {}},
          cogEmailVerified: {},
          cogUserStatus: {},
          cogUsername: {},
          email: {required, minLength: minLength(3)},
          given_name: {required, minLength: minLength(3)},
          family_name: {required, minLength: minLength(3)},
          source: {},
          createdAt: {},
          updatedAt: {},
          IPCorp: {},
          internal: {},
          TaskSaveAsTemplate: {},
          CaseSaveAsTemplate: {},
        },
      }
    },
    methods: {
      /**
       * Get Cognito Identity Service Provider
       * @returns {Promise}
       */
      async getCognitoIdentityServiceProvider() {
        return new Promise(async (resolve, reject) => {
          Auth.currentAuthenticatedUser().then(user => {
            Auth.currentCredentials()
              .then(async credentials => {
                resolve(
                  new AWS.CognitoIdentityServiceProvider({
                    apiVersion: '2016-04-18',
                    credentials: Auth.essentialCredentials(credentials),
                    region: aws_exports.aws_appsync_region
                  })
                );
              })
              .catch(err => {
                console.log('getCognitoIdentityServiceProvider - Error - currentCredentials', err);
                reject(err);
              })
          })
          .catch(err => {
            console.log('getCognitoIdentityServiceProvider - Error - currentAuthenticatedUser', err);
            reject(err);
          })
        });
      },
      /**
       * Get user attributes for Cognito
       * @param {Object} data - the user data
       * @returns {Object}
       */
      getCognitoUserParams() {
        return {
          Username: this.form.email.toLowerCase(),
          UserPoolId: aws_exports.aws_user_pools_id,
          MessageAction: 'RESEND',
          UserAttributes: [
            {
              Name: 'given_name', 
              Value: this.form.given_name
            },
            {
              Name: 'family_name', 
              Value: this.form.family_name
            },
            {
              Name: 'email', 
              Value: this.form.email.toLowerCase()
            },
            {
              Name: 'email_verified',
              Value: 'true'
            },
          ],
        };
      },
      /**
       * @summary
       * Recreate the user in Cognito Users Pool to force signup code reset
       * 
       * @description
       * For all usres created using Cognito adminCreateUser API call the only
       * to force sign up code verification is to invoke the adminCreateUser API
       * call once more
       * 
       * @see https://stackoverflow.com/questions/53194794/cant-resend-verification-code-through-aws-cognito-api
       * @see https://docs.aws.amazon.com/cli/latest/reference/cognito-idp/resend-confirmation-code.html
       * @see https://docs.aws.amazon.com/cli/latest/reference/cognito-idp/sign-up.html
       * @see https://docs.aws.amazon.com/aws-sdk-php/v3/api/api-cognito-idp-2016-04-18.html#admincreateuser
       * 
       * @returns {Void}
       */
      async resendSignupCode() {
        this.isPromisePending = true;
        this.alertModalMessage = "";
        
        const cognitoIdentityServiceProvider = await this.getCognitoIdentityServiceProvider()
          .catch(err => this.alertModalMessage = err.message);
        
        if (this.alertModalMessage) {
          this.isAlertModalActive = true;
          this.isPromisePending = false;
          return;
        }
        
        const cognitoUserParams = this.getCognitoUserParams();
        await cognitoIdentityServiceProvider
          .adminCreateUser(cognitoUserParams)
          .promise()
          .catch(err => this.alertModalMessage = err.message);

        if (this.alertModalMessage) {
          this.isAlertModalActive = true;
          this.isPromisePending = false;
          return;
        }

        this.alertModalMessage = 'Temporary Password resent successfully';
        this.isAlertModalActive = true;
        this.isPromisePending = false;
      },
      onAlertModalClose() {
        this.isAlertModalActive = false;
      },
      orderedAuditLog: function (items) {
        return _.orderBy(items, 'createdAt', 'desc');
      },
      onShowAuditLog () {
        this.auditLogDialog = true;
      }, 
      getLinkConfigs() {
        if (this.$Session.isAdminGroupMember) {
          this.auditLogList.linkConfigs = [
            {
              name: 'user',
              hrefCallback: (column, name, item) => {               
                return '#/users/' + (item.user ? item.userId : "" );
              },
              titleCallback: (column, name, item) => {                           
                  return item.user                
              },
            }
          ]

          if (this.$refs.auditLogListRef) {
            this.$refs.auditLogListRef.refresh();
          }

        }
      }      
    },
    watch: {
        '$Session.isAdminGroupMember'(){
            this.getLinkConfigs();
        }        
    }    
  }
</script>

<style lang="scss">

  .md-tabs .md-tabs-navigation {
    padding-left: 15px;
    color: black;
    display: none;

    .md-tab-nav-button.md-button {
      color: black !important;

      &.active {
        color: black !important;
        border-bottom: 2px solid black;
      }
    }
  }

  .md-content .md-tabs-container .md-tab > .md-layout-item {
    padding-left: 0;
    padding-right: 0;
  }

</style>

<style lang="scss" scoped>
  .atlas-button-blue {
    margin: 15px;
    margin-bottom: 0;
  }
  
  .dynamicList-loading {
    position: absolute;
    top: 200px;
    left: 50%;
    transform: translateX(-50%);
    bottom: auto;
    right: auto;
    display: block;
    z-index: 9999;
  }
  </style>

  <style lang="scss">
  .md-dialog._resendSignupCode {
    width: auto;

    .md-dialog-actions {
      padding-bottom: 20px;
    }
  }
</style>